import { useCallback, useEffect, useMemo, useState } from "react";
import { Link, useSearchParams } from "react-router-dom";
import api from "../../services/api";

const STATUS_OPTIONS = ["", "nieuw", "in_behandeling", "actie_uitgezet", "opgelost", "afgesloten", "geannuleerd"];
const TYPE_OPTIONS = ["", "unsafe_situation", "near_miss", "incident", "stop_work", "observation", "other"];
const SEVERITY_OPTIONS = ["", "low", "medium", "high", "critical"];

function formatDateTime(value) {
  if (!value) return "-";
  const date = new Date(value);
  if (Number.isNaN(date.getTime())) return "-";
  return date.toLocaleString("nl-NL", {
    year: "numeric",
    month: "2-digit",
    day: "2-digit",
    hour: "2-digit",
    minute: "2-digit",
  });
}

export default function KamReportsPage() {
  const [params, setParams] = useSearchParams();
  const [loading, setLoading] = useState(true);
  const [creating, setCreating] = useState(false);
  const [error, setError] = useState("");
  const [notice, setNotice] = useState("");
  const [data, setData] = useState({ items: [], pagination: { page: 1, limit: 50, total: 0, totalPages: 1 } });
  const [createType, setCreateType] = useState("unsafe_situation");
  const [createSeverity, setCreateSeverity] = useState("medium");
  const [createDescription, setCreateDescription] = useState("");
  const [createPhoto, setCreatePhoto] = useState(null);

  const filters = useMemo(
    () => ({
      status: params.get("status") || "",
      severity: params.get("severity") || "",
      type: params.get("type") || "",
      projectId: params.get("projectId") || "",
      page: Number(params.get("page") || 1),
      quickCreate: params.get("quickCreate") === "1",
      workorderId: params.get("workorderId") || "",
      clientId: params.get("clientId") || "",
      installationId: params.get("installationId") || "",
    }),
    [params]
  );

  const load = useCallback(async () => {
    try {
      setLoading(true);
      setError("");
      const query = {
        limit: 50,
        page: filters.page || 1,
        sortBy: "createdAt",
        sortDir: "desc",
      };
      if (filters.status) query.status = filters.status;
      if (filters.severity) query.severity = filters.severity;
      if (filters.type) query.type = filters.type;
      if (filters.projectId) query.projectId = filters.projectId;
      const res = await api.get("/kam/reports", { params: query });
      setData({
        items: Array.isArray(res?.data?.items) ? res.data.items : [],
        pagination: res?.data?.pagination || { page: 1, limit: 50, total: 0, totalPages: 1 },
      });
    } catch (err) {
      setError(err?.response?.data?.message || "KAM meldingen laden mislukt");
    } finally {
      setLoading(false);
    }
  }, [filters.page, filters.projectId, filters.severity, filters.status, filters.type]);

  useEffect(() => {
    load();
  }, [load]);

  function setFilter(key, value) {
    const next = new URLSearchParams(params);
    if (!value) next.delete(key);
    else next.set(key, value);
    next.set("page", "1");
    setParams(next);
  }

  function gotoPage(page) {
    const next = new URLSearchParams(params);
    next.set("page", String(page));
    setParams(next);
  }

  async function quickCreateReport(event) {
    event.preventDefault();
    if (!filters.workorderId) {
      setError("workorderId ontbreekt voor quick-create.");
      return;
    }
    if (!createDescription.trim()) {
      setError("Omschrijving is verplicht.");
      return;
    }

    try {
      setCreating(true);
      setError("");
      setNotice("");

      let photoIds = [];
      if (createPhoto) {
        const formData = new FormData();
        formData.append("workorderId", filters.workorderId);
        formData.append("category", "bewijs");
        formData.append("photo", createPhoto, createPhoto.name);
        const photoRes = await api.post("/photos/upload", formData, {
          headers: { "Content-Type": "multipart/form-data" },
        });
        const photoId = String(photoRes?.data?._id || "").trim();
        if (photoId) {
          photoIds = [photoId];
        }
      }

      const title = createDescription.trim().slice(0, 120) || "Veiligheidsmelding";
      await api.post("/kam/reports", {
        title,
        description: createDescription.trim(),
        type: createType,
        severity: createSeverity,
        workorderId: filters.workorderId,
        projectId: filters.projectId || undefined,
        clientId: filters.clientId || undefined,
        installationId: filters.installationId || undefined,
        photoIds,
      });

      setCreateDescription("");
      setCreatePhoto(null);
      setNotice("Veiligheidsmelding aangemaakt.");
      await load();
    } catch (err) {
      setError(err?.response?.data?.message || "Veiligheidsmelding aanmaken mislukt");
    } finally {
      setCreating(false);
    }
  }

  return (
    <div className="space-y-4">
      <section className="rounded-xl border bg-white p-4 shadow-sm">
        <h2 className="text-lg font-semibold text-gray-900">KAM meldingen</h2>
        <p className="text-sm text-gray-500">Filter op status, type, ernst en project.</p>
      </section>

      {filters.quickCreate ? (
        <section className="rounded-xl border border-red-200 bg-red-50 p-4 shadow-sm">
          <h3 className="text-sm font-semibold text-red-900">Veiligheidsmelding maken (werkbon)</h3>
          <p className="text-xs text-red-800">
            Werkbon: <strong>{filters.workorderId || "-"}</strong>
          </p>
          <form onSubmit={quickCreateReport} className="mt-3 grid gap-2 md:grid-cols-2">
            <FilterSelect label="Type" value={createType} onChange={setCreateType} options={TYPE_OPTIONS.filter(Boolean)} />
            <FilterSelect label="Severity" value={createSeverity} onChange={setCreateSeverity} options={SEVERITY_OPTIONS.filter(Boolean)} />
            <label className="text-xs font-medium text-gray-700 md:col-span-2">
              Korte omschrijving
              <textarea
                rows={3}
                value={createDescription}
                onChange={(event) => setCreateDescription(event.target.value)}
                className="mt-1 w-full rounded border border-red-200 p-2 text-sm text-gray-900"
              />
            </label>
            <label className="text-xs font-medium text-gray-700 md:col-span-2">
              Foto (optioneel)
              <input
                type="file"
                accept="image/*"
                onChange={(event) => setCreatePhoto(event.target.files?.[0] || null)}
                className="mt-1 w-full rounded border border-red-200 px-2 py-2 text-sm text-gray-900"
              />
            </label>
            <div className="md:col-span-2">
              <button
                type="submit"
                disabled={creating}
                className="rounded bg-red-700 px-3 py-2 text-sm text-white hover:bg-red-800 disabled:opacity-50"
              >
                {creating ? "Opslaan..." : "Veiligheidsmelding versturen"}
              </button>
            </div>
          </form>
        </section>
      ) : null}

      <section className="grid gap-2 rounded-xl border bg-white p-4 shadow-sm md:grid-cols-4">
        <FilterSelect label="Status" value={filters.status} onChange={(value) => setFilter("status", value)} options={STATUS_OPTIONS} />
        <FilterSelect label="Severity" value={filters.severity} onChange={(value) => setFilter("severity", value)} options={SEVERITY_OPTIONS} />
        <FilterSelect label="Type" value={filters.type} onChange={(value) => setFilter("type", value)} options={TYPE_OPTIONS} />
        <FilterInput label="ProjectId" value={filters.projectId} onChange={(value) => setFilter("projectId", value)} />
      </section>

      {error ? <section className="rounded-xl border border-red-200 bg-red-50 p-3 text-sm text-red-700">{error}</section> : null}
      {notice ? <section className="rounded-xl border border-emerald-200 bg-emerald-50 p-3 text-sm text-emerald-700">{notice}</section> : null}

      <section className="rounded-xl border bg-white p-4 shadow-sm">
        {loading ? <p className="text-sm text-gray-500">Laden...</p> : null}
        {!loading && data.items.length === 0 ? <p className="text-sm text-gray-500">Geen meldingen gevonden.</p> : null}

        {!loading && data.items.length > 0 ? (
          <div className="overflow-x-auto">
            <table className="w-full text-sm">
              <thead>
                <tr className="border-b text-left text-xs uppercase tracking-wide text-gray-500">
                  <th className="py-2 pr-2">Datum</th>
                  <th className="py-2 pr-2">Status</th>
                  <th className="py-2 pr-2">Severity</th>
                  <th className="py-2 pr-2">Type</th>
                  <th className="py-2 pr-2">Titel</th>
                  <th className="py-2 pr-2">Project</th>
                  <th className="py-2 pr-2">Assigned</th>
                  <th className="py-2">Actie</th>
                </tr>
              </thead>
              <tbody>
                {data.items.map((item) => {
                  const severity = String(item?.severity || "");
                  const severityClass =
                    severity === "critical"
                      ? "bg-red-100 text-red-700"
                      : severity === "high"
                      ? "bg-amber-100 text-amber-700"
                      : "bg-slate-100 text-slate-700";
                  return (
                    <tr key={item._id} className="border-b last:border-b-0">
                      <td className="py-2 pr-2 text-gray-600">{formatDateTime(item?.createdAt)}</td>
                      <td className="py-2 pr-2">{item?.status || "-"}</td>
                      <td className="py-2 pr-2">
                        <span className={`rounded-full px-2 py-1 text-xs font-medium ${severityClass}`}>{severity || "-"}</span>
                      </td>
                      <td className="py-2 pr-2">{item?.type || "-"}</td>
                      <td className="py-2 pr-2 font-medium text-gray-900">{item?.title || "-"}</td>
                      <td className="py-2 pr-2 text-gray-700">{item?.projectId?.naam || item?.projectId?.title || "-"}</td>
                      <td className="py-2 pr-2 text-gray-700">{item?.assignedTo?.naam || item?.assignedTo?.email || "-"}</td>
                      <td className="py-2">
                        <Link to={`/pl/kam/meldingen/${item._id}`} className="text-blue-700 hover:underline">
                          Open
                        </Link>
                      </td>
                    </tr>
                  );
                })}
              </tbody>
            </table>
          </div>
        ) : null}

        <div className="mt-4 flex items-center justify-between text-sm text-gray-600">
          <span>
            Totaal: {data.pagination.total} • Pagina {data.pagination.page} / {data.pagination.totalPages}
          </span>
          <div className="flex gap-2">
            <button
              type="button"
              disabled={data.pagination.page <= 1}
              onClick={() => gotoPage(data.pagination.page - 1)}
              className="rounded border border-gray-300 px-2 py-1 disabled:opacity-50"
            >
              Vorige
            </button>
            <button
              type="button"
              disabled={data.pagination.page >= data.pagination.totalPages}
              onClick={() => gotoPage(data.pagination.page + 1)}
              className="rounded border border-gray-300 px-2 py-1 disabled:opacity-50"
            >
              Volgende
            </button>
          </div>
        </div>
      </section>
    </div>
  );
}

function FilterSelect({ label, value, onChange, options }) {
  return (
    <label className="text-xs font-medium text-gray-600">
      {label}
      <select
        value={value}
        onChange={(event) => onChange(event.target.value)}
        className="mt-1 w-full rounded border border-gray-300 px-2 py-2 text-sm text-gray-900"
      >
        {options.map((option) => (
          <option key={option || "all"} value={option}>
            {option || "Alle"}
          </option>
        ))}
      </select>
    </label>
  );
}

function FilterInput({ label, value, onChange }) {
  return (
    <label className="text-xs font-medium text-gray-600">
      {label}
      <input
        type="text"
        value={value}
        onChange={(event) => onChange(event.target.value)}
        placeholder="ObjectId"
        className="mt-1 w-full rounded border border-gray-300 px-2 py-2 text-sm text-gray-900"
      />
    </label>
  );
}
